/**
 * BORN FORGED — Color variant strip (<bf-color-strip>)
 *
 * Aucun second système de sélection : un clic coche l'input (ou l'option) correspondant
 * dans le <variant-picker> natif d'Horizon et déclenche son événement "change".
 * Le thème s'occupe ensuite de tout : ProductSelectEvent, prix, SKU, stock, bouton Add to Cart,
 * input caché "id" du formulaire, ?variant= dans l'URL et remplacement de <media-gallery>
 * (qui contient ce composant, re-rendu côté serveur avec le bon état actif).
 */
(() => {
  if (customElements.get('bf-color-strip')) return;

  class BfColorStrip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.bf-color-strip__track');
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      // Garde la miniature active visible dans la rangée (mobile), sans faire défiler la page.
      requestAnimationFrame(() => this.revealActive(false));
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
    }

    get items() {
      return Array.from(this.querySelectorAll('.bf-color-strip__item'));
    }

    /** Le variant-picker natif de la même section produit. */
    findNativePicker() {
      const scope = this.closest('.shopify-section') || document;
      return scope.querySelector('variant-picker');
    }

    onClick = (event) => {
      const item = event.target.closest('.bf-color-strip__item');
      if (!item || !this.contains(item)) return;
      if (item.getAttribute('aria-checked') === 'true') return;
      this.select(item);
    };

    onKeydown = (event) => {
      const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
      if (!keys.includes(event.key)) return;
      const items = this.items;
      const current = items.indexOf(document.activeElement);
      if (current === -1) return;
      event.preventDefault();
      let next = current;
      if (event.key === 'ArrowRight') next = (current + 1) % items.length;
      if (event.key === 'ArrowLeft') next = (current - 1 + items.length) % items.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = items.length - 1;
      items[next].focus();
      this.select(items[next]);
    };

    select(item) {
      const optionValueId = item.dataset.optionValueId;
      const picker = this.findNativePicker();
      const control = picker?.querySelector(`[data-option-value-id="${optionValueId}"]`);

      if (!control) {
        // Secours : pas de sélecteur natif pour cette option → navigation vers la variante.
        if (item.dataset.variantUrl) window.location.assign(item.dataset.variantUrl);
        return;
      }

      // Retour visuel immédiat (le serveur renverra le même état lors du re-rendu de la galerie).
      this.setActive(item);

      if (control instanceof HTMLInputElement) {
        control.checked = true;
        control.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (control instanceof HTMLOptionElement) {
        const select = control.closest('select');
        if (!select) return;
        select.value = control.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    setActive(item) {
      for (const el of this.items) {
        const active = el === item;
        el.classList.toggle('is-active', active);
        el.setAttribute('aria-checked', String(active));
      }
      const label = this.querySelector('[data-bf-color-label]');
      if (label && item.dataset.value) label.textContent = item.dataset.value;
      this.classList.add('is-loading');
      this.revealActive(true);
    }

    revealActive(smooth) {
      const track = this.track;
      const active = this.querySelector('.bf-color-strip__item.is-active');
      if (!track || !active || track.scrollWidth <= track.clientWidth) return;
      const left = active.offsetLeft; // track est position: relative
      const right = left + active.offsetWidth;
      let target = null;
      if (left < track.scrollLeft) target = left - 8;
      else if (right > track.scrollLeft + track.clientWidth) target = right - track.clientWidth + 8;
      if (target === null) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      track.scrollTo({ left: Math.max(0, target), behavior: smooth && !reduce ? 'smooth' : 'auto' });
    }
  }

  customElements.define('bf-color-strip', BfColorStrip);
})();
